import React, { useState } from "react";
import { ClockIcon } from "lucide-react";
import { Button } from "../ui/button";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { UserProfile, Friend, addFriend } from "../../lib/supabase";
import { useAuth } from "../../context/AuthContext";
import toast from "react-hot-toast";

interface FriendRecommendationItemProps {
  recommendation: UserProfile;
  sentRequests: Friend[];
  onFriendAdded?: (friendId: string) => void;
}

export const FriendRecommendationItem: React.FC<
  FriendRecommendationItemProps
> = ({ recommendation, sentRequests, onFriendAdded }) => {
  const { user } = useAuth();
  const [isAdding, setIsAdding] = useState(false);

  // Check if this user has a pending request sent by the current user
  const hasPendingRequest = sentRequests.some(
    req => req.friend_id === recommendation.id
  );

  // Get avatar URL with fallback
  const getAvatarUrl = () => {
    if (recommendation.avatar_url) {
      return recommendation.avatar_url;
    }
    return "/avatars/ava1.png";
  };

  const handleAddFriend = async () => {
    if (!user?.id || isAdding || hasPendingRequest) return;

    setIsAdding(true);

    try {
      const result = await addFriend(user.id, recommendation.id);

      if (result.success) {
        toast.success(
          `Permintaan pertemanan dikirim ke ${recommendation.name}`
        );

        if (onFriendAdded) {
          onFriendAdded(recommendation.id);
        }
      } else {
        toast.error(result.error || "Gagal menambahkan teman");
      }
    } catch (error) {
      console.error("Error adding friend:", error);
      toast.error("Terjadi kesalahan saat menambahkan teman");
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <div className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg transition-colors duration-200">
      {/* Avatar */}
      <Avatar className="w-8 h-8 flex-shrink-0">
        <AvatarFallback>
          <img
            className="w-full h-full object-cover"
            alt="User Avatar"
            src={getAvatarUrl()}
          />
        </AvatarFallback>
      </Avatar>

      {/* User Info */}
      <div className="flex-1 min-w-0">
        <h4 className="font-medium text-[#373e46] text-sm truncate">
          {recommendation.name}
        </h4>
        <p className="text-xs text-gray-500 truncate">
          {recommendation.asal_sekolah}
        </p>
        <p className="text-xs text-gray-500 truncate">{recommendation.role}</p>
      </div>

      {/* Add Button */}
      <Button
        onClick={handleAddFriend}
        disabled={isAdding || hasPendingRequest}
        size="sm"
        className={`px-3 py-1 rounded-lg font-medium transition-all duration-300 text-xs h-7 flex items-center gap-1 ${
          hasPendingRequest
            ? "bg-yellow-100 text-yellow-800 cursor-default"
            : isAdding
              ? "bg-gray-300 text-gray-500 cursor-not-allowed"
              : "bg-gradient-to-r from-[#6b2cfe] to-[#0217ff] hover:opacity-90 text-white hover:shadow-lg"
        }`}
      >
        {isAdding ? (
          <>
            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
            <span>Adding...</span>
          </>
        ) : hasPendingRequest ? (
          <>
            <ClockIcon className="w-3 h-3" />
            <span>Pending</span>
          </>
        ) : (
          <span>Tambah</span>
        )}
      </Button>
    </div>
  );
};
